import React from 'react';
import Button from 'Components/DesignSystem/Button/Button';
import Spinner from 'Components/DesignSystem/Spinner/Spinner';
import ErrorState from 'Components/DesignSystem/ErrorState/ErrorState';
import { useResolveConfig } from '../context/ResolveConfigContext';
import ResolveGenericSection from './ResolveGenericSection';

/** `product_card` is rendered by `ResolveProductCardButton`'s own header dialog, never by
 * this generic loop — see `ResolveView.tsx`. */
const EXCLUDED_SECTION_NAMES = new Set(['product_card']);

/** The Resolve Configurations screen: every Configuration section renders from the
 * backend's own manifest schema via `ResolveGenericSection` — a setting added, edited or
 * removed directly in the (Mongo-backed) manifest appears here with no frontend code
 * change. See `okf/resolve/e2e.md`. */
const ResolveConfigPage: React.FC = () => {
  const { loadState, sections, isDirty, saving, save, reload, canUpdate } = useResolveConfig();

  if (loadState === 'loading') {
    return (
      <div className="tw-flex tw-justify-center tw-py-10">
        <Spinner size="md" label="Loading Resolve configuration…" showLabel data-testid="resolve-config-loading" />
      </div>
    );
  }

  if (loadState === 'forbidden') {
    return (
      <ErrorState
        title="You don't have permission to view this configuration"
        description="Ask an admin to grant you access to Resolve configuration for this Environment."
        data-testid="resolve-config-forbidden"
      />
    );
  }

  if (loadState === 'error') {
    return (
      <ErrorState
        title="Failed to load the Resolve configuration"
        description="Please try again."
        action={
          <Button variant="primary" onClick={reload} data-testid="resolve-config-error-retry-btn">
            Retry
          </Button>
        }
        data-testid="resolve-config-error"
      />
    );
  }

  return (
    <div data-testid="resolve-config-content">
      <div className="tw-pt-4">
        {sections
          .filter((section) => !EXCLUDED_SECTION_NAMES.has(section.name))
          .map((section) => (
            <ResolveGenericSection section={section} key={section.name} />
          ))}
      </div>
      {canUpdate && (
        <div className="tw-flex tw-items-center tw-gap-2.5 tw-mt-4 tw-pb-6">
          <Button onClick={save} disabled={!isDirty || saving} loading={saving} data-testid="resolve-config-save-btn">
            Save
          </Button>
        </div>
      )}
    </div>
  );
};

export default ResolveConfigPage;
